import { Monitor, Smartphone } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FrameDevice } from '../lib/frame-viewport';
import { useWebsiteT } from '../hooks/use-website-t';

export interface DeviceToggleProps {
  readonly device: FrameDevice;
  readonly onChange: (device: FrameDevice) => void;
}

/**
 * Desktop or mobile view of the SAME frame. Switching changes only the width
 * of the wrapper: the iframe element, its route and its edit state stay.
 */
export function DeviceToggle({ device, onChange }: DeviceToggleProps): React.JSX.Element {
  const { t } = useWebsiteT();
  const options = [
    { value: 'desktop', label: t('admin.websiteTemplates.editor.device.desktop'), Icon: Monitor },
    { value: 'mobile', label: t('admin.websiteTemplates.editor.device.mobile'), Icon: Smartphone },
  ] as const;

  return (
    <div
      role="group"
      aria-label={t('admin.websiteTemplates.frame.viewport')}
      className="border-input bg-card inline-flex rounded-lg border p-0.5"
    >
      {options.map(({ value, label, Icon }) => (
        <button
          key={value}
          type="button"
          aria-pressed={device === value}
          aria-label={label}
          title={label}
          data-testid={`device-toggle-${value}`}
          onClick={() => onChange(value)}
          className={cn(
            'focus-visible:ring-ring/50 inline-flex size-8 items-center justify-center rounded-md outline-none transition-colors focus-visible:ring',
            device === value
              ? 'bg-admin-lime text-primary-foreground'
              : 'text-muted-foreground hover:bg-admin-surface-muted',
          )}
        >
          <Icon aria-hidden="true" className="size-4" />
        </button>
      ))}
    </div>
  );
}
